/**
 * AuthContext - Authentication Provider (Backend-Proxied)
 *
 * All auth operations (login, signup, signout, reset, etc.) are routed
 * through the Bun.js backend API instead of calling Supabase directly.
 *
 * Session tokens are stored in localStorage for persistence across
 * page reloads. No Supabase client is used on the frontend.
 *
 * Recovery flow (password reset):
 *   When the user clicks the recovery link from the email, Supabase redirects
 *   to /reset-password#access_token=...&refresh_token=...&type=recovery
 *   This provider detects hash tokens on mount, applies the session, and
 *   cleans up the URL hash so ResetPasswordPage can show the form.
 *
 * Usage:
 *   const { user, signIn, signOut, loading } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setAuthToken, setOnUnauthorized, setOnPasswordChangeRequired } from '@/services/apiClient';
import { trackEvent } from '@/services/analyticsService';
import { getAttribution } from '@/hooks/useUtmCapture';
import {
  IMPERSONATION_PATH,
  bootstrapImpersonation,
  clearImpersonation,
  type ImpersonationState,
} from '@/lib/impersonation';

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY_USER = 'cai_auth_user';
const STORAGE_KEY_SESSION = 'cai_auth_session';

// ============================================================================
// TYPES
// ============================================================================

export interface AuthUser {
  id: string;
  email?: string;
  /** Senha temporária gerada pelo suporte (app_metadata.must_change_password). */
  mustChangePassword?: boolean;
}

interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number;
  token_type: string;
}

interface AuthError {
  message: string;
  code?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string, phone?: string, turnstileToken?: string | null) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null; message?: string }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  /** Troca obrigatória pendente (signin/refresh ou 403 must_change_password da API). */
  mustChangePassword: boolean;
  /** Marca a troca como pendente (ex.: admin resetou enquanto o usuário estava logado). */
  markPasswordChangeRequired: () => void;
  isConfigured: boolean;
  /** Sessão de suporte desta aba ("entrar como usuário"), ou null. */
  impersonation: ImpersonationState | null;
  /** Encerra a sessão de suporte desta aba (fecha a aba ou volta ao admin). */
  endImpersonation: () => Promise<void>;
}

// ============================================================================
// API HELPERS
// ============================================================================

const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_URL || '';
};

async function authFetch<T = any>(
  path: string,
  body: Record<string, any>,
  token?: string | null,
): Promise<{ data: T | null; error: AuthError | null }> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      // GoTrue recusa reaproveitar a senha atual (inclusive a temporária).
      if (data.code === 'same_password') {
        return { data: null, error: { message: 'auth.errors.samePassword', code: data.code } };
      }
      return { data: null, error: { message: data.error || 'auth.errors.unknown', code: typeof data.code === 'string' ? data.code : undefined } };
    }

    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: { message: 'auth.errors.network' } };
  }
}

// ============================================================================
// LOCAL STORAGE HELPERS
// ============================================================================

function saveToStorage(user: AuthUser, session: AuthSession): void {
  try {
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(session));
  } catch {
    // localStorage might be unavailable (private browsing, etc.)
  }
}

function loadFromStorage(): { user: AuthUser | null; session: AuthSession | null } {
  try {
    const userStr = localStorage.getItem(STORAGE_KEY_USER);
    const sessionStr = localStorage.getItem(STORAGE_KEY_SESSION);

    if (!userStr || !sessionStr) return { user: null, session: null };

    return {
      user: JSON.parse(userStr) as AuthUser,
      session: JSON.parse(sessionStr) as AuthSession,
    };
  } catch {
    return { user: null, session: null };
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_SESSION);
  } catch {
    // ignore
  }
}

// ============================================================================
// URL HASH TOKEN HELPERS (for Supabase recovery redirect)
// ============================================================================

interface HashTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  type: string; // e.g. 'recovery'
}

/**
 * Parse authentication tokens from the URL hash fragment.
 * Supabase redirects to /reset-password#access_token=...&refresh_token=...&type=recovery
 * Returns null if no valid tokens are found.
 */
function parseHashTokens(): HashTokens | null {
  try {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return null;

    const params = new URLSearchParams(hash.substring(1));
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const expires_in = params.get('expires_in');
    const token_type = params.get('token_type');
    const type = params.get('type');

    if (!access_token || !refresh_token) return null;

    return {
      access_token,
      refresh_token,
      expires_in: expires_in ? parseInt(expires_in, 10) : 3600,
      token_type: token_type || 'bearer',
      type: type || '',
    };
  } catch {
    return null;
  }
}

/**
 * Remove the hash fragment from the URL without triggering a navigation.
 * This prevents tokens from remaining visible in the address bar or browser history.
 */
/**
 * Aba de impersonate: lida uma vez, antes do primeiro render. O hash
 * `#imp=...` é consumido aqui, então parseHashTokens não o vê.
 */
function readImpersonationOnLoad(): ImpersonationState | null {
  if (typeof window === 'undefined') return null;
  try {
    return bootstrapImpersonation({ hash: window.location.hash, storage: window.sessionStorage, clearHash: clearUrlHash });
  } catch {
    return null;
  }
}

/** Sessão sintética para o resto do app; sem refresh_token (não há renovação). */
function impersonationSession(state: ImpersonationState): AuthSession {
  const expiresAt = Math.floor(Date.parse(state.expiresAt) / 1000);
  return {
    access_token: state.token,
    refresh_token: '',
    expires_in: Math.max(0, expiresAt - Math.floor(Date.now() / 1000)),
    expires_at: expiresAt,
    token_type: 'impersonation',
  };
}

function clearUrlHash(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    // Fallback: at least clear the hash
    window.location.hash = '';
  }
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Aba de impersonate: usa só a sessão de suporte (sessionStorage) e nunca lê
  // nem escreve a sessão do admin no localStorage.
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(readImpersonationOnLoad);
  const impersonationRef = useRef(impersonation);
  impersonationRef.current = impersonation;
  // Fixo por carga de página: depois que a sessão de suporte acaba, impersonation
  // vira null antes do location.replace concluir; um 401 atrasado nessa janela
  // não pode cair no clearStorage() e deslogar o admin nas outras abas.
  const supportTabRef = useRef(impersonation !== null);

  // Load from localStorage synchronously so user is available on first render.
  // This prevents race conditions where child components see user=null and redirect.
  const [user, setUser] = useState<AuthUser | null>(() => {
    if (impersonation) return { id: impersonation.target.id, email: impersonation.target.email ?? undefined };
    const { user } = loadFromStorage();
    return user;
  });
  const [session, setSession] = useState<AuthSession | null>(() => {
    if (impersonation) {
      setAuthToken(impersonation.token);
      return impersonationSession(impersonation);
    }
    const { session } = loadFromStorage();
    if (session?.access_token) {
      // Set API client token immediately so first API calls are authenticated
      setAuthToken(session.access_token);
    }
    return session;
  });
  // Start loading=true when there's a stored session to validate OR when there
  // are hash tokens in the URL (recovery redirect). This prevents ProtectedRoute
  // and ResetPasswordPage from rendering before we process the session.
  const [loading, setLoading] = useState<boolean>(() => {
    if (impersonation) return false;
    const { session } = loadFromStorage();
    const hasHash = typeof window !== 'undefined' && window.location.hash.includes('access_token');
    return !!session || hasHash;
  });
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Store session in state, localStorage, and API client.
   * Also schedules token refresh.
   */
  const applySession = useCallback(async (
    newUser: AuthUser,
    newSession: AuthSession,
  ) => {
    setUser(newUser);
    setSession(newSession);

    // Set token on the API client for authenticated requests
    setAuthToken(newSession.access_token);

    // Persist to localStorage for page reload recovery
    saveToStorage(newUser, newSession);

    // Schedule token refresh before expiry
    scheduleRefresh(newSession);
  }, []);

  /**
   * Schedule a token refresh slightly before expiry.
   */
  const scheduleRefresh = useCallback((sess: AuthSession) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Refresh 60 seconds before expiry
    const expiresInMs = (sess.expires_in - 60) * 1000;
    if (expiresInMs <= 0) return;

    refreshTimeoutRef.current = setTimeout(async () => {
      const { data, error } = await authFetch<{
        user: AuthUser;
        session: AuthSession;
      }>('/api/auth/refresh', { refresh_token: sess.refresh_token });

      if (!error && data?.session && data?.user) {
        await applySession(data.user, data.session);
      } else {
        // Refresh failed - session expired
        clearSession();
      }
    }, expiresInMs);
  }, [applySession]);

  /**
   * Clear all auth state.
   */
  const clearSession = useCallback(() => {
    setUser(null);
    setSession(null);
    setAuthToken(null);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    if (impersonationRef.current || supportTabRef.current) {
      // Nunca apagar a sessão do admin (localStorage) a partir da aba de suporte.
      clearImpersonation(window.sessionStorage);
      setImpersonation(null);
      window.location.replace(`${IMPERSONATION_PATH}?ended=1`);
      return;
    }
    clearStorage();
  }, []);

  // ========================================================================
  // VALIDATE SESSION ON MOUNT
  // 1. Check for hash tokens (Supabase recovery/magic-link redirect)
  // 2. If no hash, validate existing session from localStorage
  // 3. Set loading=false only after all processing is done
  // ========================================================================

  const hasValidatedRef = useRef(false);

  useEffect(() => {
    // Only run once on mount
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;

    const initializeAuth = async () => {
      // Aba de impersonate: sem refresh nem validação de sessão Supabase.
      if (impersonationRef.current) {
        setLoading(false);
        return;
      }

      // -----------------------------------------------------------------
      // STEP 1: Check for hash tokens from Supabase redirect
      // When user clicks the recovery link, Supabase redirects to:
      //   /reset-password#access_token=xxx&refresh_token=xxx&type=recovery
      // -----------------------------------------------------------------
      const hashTokens = parseHashTokens();

      if (hashTokens && hashTokens.access_token) {
        // Clear hash immediately for security (remove tokens from URL bar)
        clearUrlHash();

        // Clear any existing session before applying recovery session
        clearSession();

        // Build session object from hash tokens
        const now = Math.floor(Date.now() / 1000);
        const newSession: AuthSession = {
          access_token: hashTokens.access_token,
          refresh_token: hashTokens.refresh_token,
          expires_in: hashTokens.expires_in,
          expires_at: now + hashTokens.expires_in,
          token_type: hashTokens.token_type,
        };

        // Validate the token by fetching user info from the backend.
        // We temporarily set the auth token so the API client can make
        // the request, then verify the token by calling a simple endpoint.
        setAuthToken(newSession.access_token);

        // Use the refresh endpoint to validate and get proper user info.
        // This also gives us a fresh session with correct expiry.
        const { data, error } = await authFetch<{
          user: AuthUser;
          session: AuthSession;
        }>('/api/auth/refresh', { refresh_token: newSession.refresh_token });

        if (!error && data?.session && data?.user) {
          await applySession(data.user, data.session);
        } else {
          // Refresh failed — try to use the token directly.
          // Build a minimal user from the token (the backend will validate
          // on updatePassword call).
          // We still apply the session so ResetPasswordPage can show the form.
          const minimalUser: AuthUser = { id: 'recovery', email: undefined };
          await applySession(minimalUser, newSession);
        }

        setLoading(false);
        return;
      }

      // -----------------------------------------------------------------
      // STEP 2: No hash tokens — validate existing session from localStorage
      // -----------------------------------------------------------------
      if (!session?.access_token) {
        setLoading(false);
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const isExpired = session.expires_at ? now >= session.expires_at : false;

      if (isExpired && session.refresh_token) {
        // Token expired — try to refresh
        const { data, error } = await authFetch<{
          user: AuthUser;
          session: AuthSession;
        }>('/api/auth/refresh', { refresh_token: session.refresh_token });

        if (!error && data?.session && data?.user) {
          await applySession(data.user, data.session);
        } else {
          // Refresh failed — force logout
          clearSession();
        }
      } else if (isExpired) {
        // Expired but no refresh token — force logout
        clearSession();
      } else {
        // Token still valid — schedule refresh before expiry
        scheduleRefresh(session);
      }

      setLoading(false);
    };

    initializeAuth();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ========================================================================
  // 401 INTERCEPTOR — clear session when API returns Unauthorized
  // ========================================================================

  useEffect(() => {
    setOnUnauthorized(() => {
      clearSession();
    });
    return () => setOnUnauthorized(null);
  }, [clearSession]);

  // ========================================================================
  // 403 must_change_password INTERCEPTOR — força /change-password
  // ========================================================================

  const markPasswordChangeRequired = useCallback(() => {
    if (impersonationRef.current) return;
    setUser((current) => {
      if (!current || current.mustChangePassword) return current;
      const next = { ...current, mustChangePassword: true };
      const { session: stored } = loadFromStorage();
      if (stored) saveToStorage(next, stored);
      return next;
    });
  }, []);

  useEffect(() => {
    setOnPasswordChangeRequired(markPasswordChangeRequired);
    return () => setOnPasswordChangeRequired(null);
  }, [markPasswordChangeRequired]);

  // ========================================================================
  // AUTH METHODS (all proxied through backend API)
  // ========================================================================

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await authFetch<{
      user: AuthUser;
      session: AuthSession;
    }>('/api/auth/signin', { email, password });

    if (error) return { error };

    if (data?.session && data?.user) {
      await applySession(data.user, data.session);
    }

    return { error: null };
  }, [applySession]);

  const signUp = useCallback(async (email: string, password: string, firstName?: string, lastName?: string, phone?: string, turnstileToken?: string | null) => {
    // Include attribution data (UTMs, referrer, etc.) captured by useUtmCapture
    const attribution = getAttribution();

    const { data, error } = await authFetch<{
      user: AuthUser;
      session: AuthSession | null;
      message?: string;
    }>('/api/auth/signup', {
      email,
      password,
      firstName,
      lastName,
      phone,
      turnstileToken: turnstileToken || undefined,
      ...attribution,
    });

    if (error) return { error };

    // If session was returned, auto-login
    if (data?.session && data?.user) {
      await applySession(data.user, data.session);
      trackEvent('signup', { email });
    }

    return { error: null };
  }, [applySession]);

  const endImpersonation = useCallback(async () => {
    const state = impersonationRef.current;
    if (!state) return;
    // A API trata /api/impersonation/end com token imp_ como "encerrar sessão".
    await authFetch('/api/impersonation/end', {}, state.token).catch(() => {});
    clearSession();
  }, [clearSession]);

  const signOut = useCallback(async () => {
    if (impersonationRef.current) {
      await endImpersonation();
      return;
    }
    const token = session?.access_token;
    // Clear local state immediately
    clearSession();
    // Notify backend (fire-and-forget)
    if (token) {
      authFetch('/api/auth/signout', {}, token).catch(() => {});
    }
  }, [session, clearSession, endImpersonation]);

  const resetPassword = useCallback(async (email: string) => {
    const { data, error } = await authFetch<{ message: string }>(
      '/api/auth/reset-password',
      { email },
    );
    if (error) return { error };
    return { error: null, message: data?.message };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (impersonationRef.current) {
      return { error: { message: 'auth.errors.impersonation', code: 'impersonation_forbidden' } };
    }
    const token = session?.access_token;
    if (!token) {
      return { error: { message: 'auth.errors.noSession' } };
    }

    const { data, error } = await authFetch<{
      message: string;
      user?: AuthUser;
      session?: AuthSession | null;
    }>(
      '/api/auth/update-password',
      { password: newPassword },
      token,
    );

    if (error) return { error };

    // Backend issues a fresh session after password change. Apply it so the
    // in-memory session/user replaces any stale recovery/invite-link state.
    // Without this, downstream API calls can drift out of sync with Supabase
    // once the short-lived recovery access_token expires.
    if (data?.session && data?.user) {
      // A resposta já vem com mustChangePassword=false (flag limpa no servidor).
      await applySession({ ...data.user, mustChangePassword: false }, data.session);
    } else {
      // Senha trocada mas sem sessão nova: a flag já foi limpa no servidor.
      setUser((current) => {
        if (!current) return current;
        const next = { ...current, mustChangePassword: false };
        const { session: stored } = loadFromStorage();
        if (stored) saveToStorage(next, stored);
        return next;
      });
    }

    return { error: null };
  }, [session, applySession]);

  // ========================================================================
  // CONTEXT VALUE
  // ========================================================================

  const value: AuthContextType = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    mustChangePassword: !!user?.mustChangePassword,
    markPasswordChangeRequired,
    isConfigured: true, // Always configured — backend handles Supabase
    impersonation,
    endImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

const FALLBACK_CONTEXT: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  signIn: async () => ({ error: { message: 'Not initialized' } }),
  signUp: async () => ({ error: { message: 'Not initialized' } }),
  signOut: async () => {},
  resetPassword: async () => ({ error: { message: 'Not initialized' } }),
  updatePassword: async () => ({ error: { message: 'Not initialized' } }),
  mustChangePassword: false,
  markPasswordChangeRequired: () => {},
  isConfigured: false,
  impersonation: null,
  endImpersonation: async () => {},
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    return FALLBACK_CONTEXT;
  }
  return context;
};
